// @flow
/* eslint jsx-a11y/click-events-have-key-events: off */
/* eslint jsx-a11y/no-static-element-interactions: off */
/* eslint jsx-a11y/no-noninteractive-element-interactions: off */
/* eslint css-modules/no-unused-class: off */

import * as React from 'react';
import classnames from 'classnames';

import { KeyEvent } from '../util/keyTypes';
import scopeTab from './scopeTab';
import * as focusManager from './focusManager';
import { getScrollbarWidth, doesScrollbarTakeWidth } from './helpers';

import { PageHidden } from '../PageHidden';
import styles from './style.scss';

const Sizes = Object.freeze({
  Small: 'Small',
  Medium: 'Medium',
  Large: 'Large',
});

// States
const Showing = true;
const Hidden = false;

// Messages and constructor
const Show = 'SHOW';
const Hide = 'HIDE';

const Message = (message: string) => ({ message });

const Actions = {
  Show: Message(Show),
  Hide: Message(Hide),
};

const transition = (old: boolean, msg: { message: string }) => {
  const { message } = msg;

  switch (message) {
    case Hide:
      return Hidden;

    case Show:
      return Showing;

    default:
      throw new Error(`Unknown message type: ${message}`);
  }
};

// change represents a static message waiting for an event to fire
const change = msg => (onUpdate: Function) => (evt: SyntheticEvent<*>) => {
  onUpdate(transition(true, msg), msg, evt);
};

const Events = {
  show: change(Actions.Show),
  hide: change(Actions.Hide),
};

export type DialogPropTypes = {|
  /** Sets size of dialog. There are 2 sizes: Small or Medium. Noted that small dialog will not have X button in the header */
  size: $Values<typeof Sizes>,
  /** Indicates if clicking outside of dialog or pressing esc key can close that dialog or not. If set, it also removes the X to close button in the dialog header */
  disableDefaultClose: boolean,
  /** Callback function for when the Dialog state changes */
  onUpdate: (dialogState: boolean, msg: { message: string }, evt: SyntheticEvent<*>) => void,
  /** Dialog contents */
  children: React.Node,
|};

class Dialog extends React.Component<DialogPropTypes> {
  _dialog: ?HTMLDivElement;

  static defaultProps = {
    size: Sizes.Medium,
    disableDefaultClose: false,
  };

  contentHasFocus() {
    if (this._dialog) {
      return (
        document.activeElement === this._dialog || this._dialog.contains(document.activeElement)
      );
    }
    return true;
  }

  // Don't steal focus from inner elements
  focusContent() {
    return this._dialog && !this.contentHasFocus() && this._dialog.focus();
  }

  componentDidMount() {
    if (this._dialog) {
      focusManager.setupScopedFocus(this._dialog);
      focusManager.markForFocusLater();

      this.focusContent();

      if (doesScrollbarTakeWidth()) {
        const { body } = window.document;
        const bodyPad = parseInt(body.style.paddingRight || 0, 10);
        body.style.paddingRight = `${bodyPad + getScrollbarWidth()}px`;
      }

      window.document.body.style.setProperty('overflow', 'hidden');
    }
  }

  componentWillUnmount() {
    window.document.body.style.removeProperty('overflow');
    window.document.body.style.removeProperty('padding-right');

    if (this._dialog) {
      focusManager.returnFocus();
      focusManager.teardownScopedFocus();
    }
  }

  closeDialog = (e: SyntheticEvent<*>) => {
    const { onUpdate } = this.props;
    e.preventDefault();
    Events.hide(onUpdate)(e);
  };

  onAutoClose = (e: SyntheticEvent<*>) => {
    if (!this.props.disableDefaultClose) {
      this.closeDialog(e);
    }
  };

  handleKeyDown = (e: SyntheticKeyboardEvent<HTMLDivElement>) => {
    if (KeyEvent.isTab(e)) {
      if (this._dialog) {
        scopeTab(this._dialog, e);
      }
      return;
    }
    if (KeyEvent.isEscape(e)) {
      this.onAutoClose(e);
    }
  };

  getDialogRef = (ref: ?HTMLDivElement) => {
    this._dialog = ref;
  };

  render() {
    const { size, children, disableDefaultClose } = this.props;

    const classes = classnames(styles.wrapper, styles[`dialog${size || ''}`]);

    return (
      <PageHidden>
        <div onClick={this.onAutoClose} className={styles.backdrop} />
        <div
          role="dialog"
          className={classes}
          onKeyDown={this.handleKeyDown}
          ref={this.getDialogRef}
          tabIndex="-1"
        >
          <div className={styles.dialog}>
            <div className={styles.dialogHead}>
              {size !== Sizes.Small && !disableDefaultClose && (
                <button
                  className={styles.close}
                  onClick={this.closeDialog}
                  data-testid="closeDialog"
                />
              )}
            </div>
            <div className={styles.dialogContent}>{children}</div>
          </div>
        </div>
      </PageHidden>
    );
  }
}

export default Dialog;
